import { useNavigationState } from '../navigation/NavigationProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CollectionSummary } from '@qkindex/contracts';
import { doc, onSnapshot } from 'firebase/firestore';
import { Item } from '../../domain/item';
import { db } from '../../firebase';
import { mapItemDocument } from '../../repositories/mappers';
import { Location } from '../../domain/location';
import {
  subscribeToSharedCollection as subscribeToCollection,
  subscribeToSharedCollectionMembership as subscribeToCollectionMembership,
} from '../collection/collectionData';
import { useCollectionCapacityCapability } from '../capacity/useCollectionCapacityCapability';
import { collectionCapabilities } from '../collection/collectionCapabilities';
import { recordKey, scopeRecord } from '../collection/multiCollectionIdentity';
import { RapidCaptureQueueDialog } from './RapidCaptureQueueDialog';
import { useRapidCaptureOutbox } from './useRapidCaptureOutbox';
import { rapidCaptureDb } from './rapidCaptureDb';
import { useFeedback } from '../feedback/FeedbackProvider';
import {
  captureDraftFields,
  ItemEditEnqueue,
  RapidCaptureDraft,
} from './rapidCaptureTypes';
import {
  projectCaptureInventory,
  mergeCaptureInventory,
} from './captureInventory';
import { useCaptureInventoryImages } from './useCaptureInventoryImages';
import { logger } from '../../services/logger';
import { RegisterCaptureQueue } from './CaptureQueueBadge';
import { captureActivityPresentation } from './captureActivityPresentation';

export type CaptureEnqueue = (draft: RapidCaptureDraft) => Promise<unknown>;

interface Props {
  userId: string;
  collections: readonly CollectionSummary[];
  locations: Location[];
  items?: Item[];
  captureCollectionId?: string;
  registerEnqueue: (
    collectionId: string,
    enqueue: CaptureEnqueue | undefined,
  ) => void;
  registerEdit?: (
    collectionId: string,
    save: ItemEditEnqueue | undefined,
  ) => void;
  onOpenItem?: (item: Item) => void;
  registerCaptureQueue?: RegisterCaptureQueue;
  onDraftItemsChange?: (collectionId: string, items: Item[]) => void;
}

/** Collection queues keep processing after the Add Item dialog closes. */
function CollectionCaptureWorker({
  collection,
  userId,
  locations,
  items,
  registerEnqueue,
  registerEdit,
  onOpenItem,
  captureCollectionId,
  registerCaptureQueue,
  onDraftItemsChange,
}: Omit<Props, 'collections' | 'items'> & {
  collection: CollectionSummary;
  items: Item[];
}) {
  const { notify } = useFeedback();
  const [membership, setMembership] = useState<{
    role: CollectionSummary['role'];
    version: number;
  }>();
  const [lifecycle, setLifecycle] = useState<{
    state: CollectionSummary['lifecycle'];
    version: number;
  }>();
  const [queue, setQueue] = useNavigationState<{
    userId: string;
    collectionId: string;
  } | null>('capture.queue', null);
  const queueOpen =
    queue?.userId === userId && queue.collectionId === collection.collectionId;
  const setQueueOpen = useCallback(
    (open: boolean) =>
      setQueue(open ? { userId, collectionId: collection.collectionId } : null),
    [collection.collectionId, setQueue, userId],
  );
  const [queuedItems, setQueuedItems] = useState<Item[]>([]);
  const collectionId = collection.collectionId;

  useEffect(() => {
    setMembership(undefined);
    setLifecycle(undefined);
    const stopMembership = subscribeToCollectionMembership(
      collectionId,
      userId,
      (value) => {
        if (
          !value ||
          value.status !== 'active' ||
          (value.role !== 'owner' &&
            value.role !== 'administrator' &&
            value.role !== 'editor')
        ) {
          setMembership(undefined);
          void rapidCaptureDb
            .purgeCollectionAuthority(
              userId,
              collectionId,
              value?.membershipVersion ?? 0,
              'revoked',
            )
            .catch(() => logger.error('Capture authority cleanup failed.'));
          return;
        }
        setMembership({ role: value.role, version: value.membershipVersion });
      },
      () => setMembership(undefined),
    );
    const stopCollection = subscribeToCollection(
      collectionId,
      userId,
      (value) => {
        if (!value || value.lifecycle !== 'active') {
          setLifecycle(undefined);
          void rapidCaptureDb
            .purgeCollectionAuthority(
              userId,
              collectionId,
              0,
              'collection-deleting',
            )
            .catch(() => logger.error('Capture lifecycle cleanup failed.'));
          return;
        }
        setLifecycle({
          state: value.lifecycle,
          version: value.lifecycleVersion,
        });
      },
      () => setLifecycle(undefined),
    );
    return () => {
      stopMembership();
      stopCollection();
    };
  }, [collectionId, userId]);

  const authority = useMemo(
    () =>
      membership && lifecycle
        ? {
            userId,
            collectionId,
            compatibilityMode: 'collection-only' as const,
            membershipVersion: membership.version,
            lifecycleVersion: lifecycle.version,
          }
        : undefined,
    [collectionId, lifecycle, membership, userId],
  );
  const rawItems = useMemo(
    () => items.filter((item) => item.collectionId === collectionId),
    [collectionId, items],
  );
  const authoritativeItems = useMemo(
    () => mergeCaptureInventory(rawItems, queuedItems),
    [rawItems, queuedItems],
  );
  // Completion must wait until the main inventory reader observes the saved item.
  const outbox = useRapidCaptureOutbox(authority, rawItems, true);
  const markItemUnavailable = outbox.markItemUnavailable;
  const openQueue = useCallback(() => setQueueOpen(true), [setQueueOpen]);
  const recordsRef = useRef(outbox.records);
  recordsRef.current = outbox.records;
  const imagesCollection = useMemo(
    () =>
      authority && membership && lifecycle
        ? {
            ...collection,
            role: membership.role,
            membershipVersion: membership.version,
            lifecycle: lifecycle.state,
            lifecycleVersion: lifecycle.version,
          }
        : undefined,
    [authority, collection, membership, lifecycle],
  );
  const visibleRecords = outbox.records.filter(
    (record) =>
      record.schemaVersion === 6 &&
      record.state !== 'terminal' &&
      record.userId === userId &&
      record.collectionId === collectionId &&
      record.membershipVersion === authority?.membershipVersion &&
      record.lifecycleVersion === authority?.lifecycleVersion,
  );
  const images = useCaptureInventoryImages(
    userId,
    imagesCollection,
    visibleRecords,
    authoritativeItems,
  );
  const holdDraft = outbox.holdDraft;
  const draftItems = useMemo(() => {
    const projected = projectCaptureInventory({
      records: outbox.records,
      items: authoritativeItems,
      authority,
      images,
      online: outbox.online,
      openQueue,
      hold: holdDraft,
    });
    // Keep the same photo URL after completion; changing URLs restarts image loading.
    const activeIds = new Set(projected.map((item) => item.id));
    const bridges = authority
      ? rawItems.flatMap((item) => {
          const image = images.get(`item:${item.id}:${item.storagePath}`);
          return image && !item.deleted && !activeIds.has(item.id)
            ? [{ ...item, image, imageLoading: false, requestImage: undefined }]
            : [];
        })
      : [];
    return [...projected, ...bridges].map((item) =>
      scopeRecord(item, collection),
    );
  }, [
    outbox.records,
    outbox.online,
    authoritativeItems,
    authority,
    images,
    openQueue,
    holdDraft,
    rawItems,
    collection,
  ]);
  useEffect(() => {
    onDraftItemsChange?.(collectionId, draftItems);
  }, [onDraftItemsChange, collectionId, draftItems]);
  useEffect(
    () => () => {
      onDraftItemsChange?.(collectionId, []);
    },
    [onDraftItemsChange, collectionId],
  );
  const count = outbox.records.length;
  const attention = outbox.records.filter(
    (record) =>
      captureActivityPresentation(record, outbox.online).severity !== 'info',
  ).length;
  const errors = outbox.records.filter(
    (record) =>
      captureActivityPresentation(record, outbox.online).severity === 'error',
  ).length;
  useEffect(() => {
    registerCaptureQueue?.(
      userId,
      collectionId,
      count
        ? {
            userId,
            collectionId,
            name: collection.name,
            count,
            attention,
            errors,
            open: openQueue,
          }
        : undefined,
    );
    return () => registerCaptureQueue?.(userId, collectionId);
  }, [
    registerCaptureQueue,
    userId,
    collectionId,
    collection.name,
    count,
    attention,
    errors,
    openQueue,
  ]);
  const queuedItemIds = JSON.stringify(
    outbox.records
      .filter((record) => record.state !== 'terminal')
      .map((record) => record.itemId)
      .sort(),
  );
  const membershipVersion = membership?.version;
  const lifecycleVersion = lifecycle?.version;
  const queuedScope = useRef('');
  useEffect(() => {
    if (membershipVersion === undefined || lifecycleVersion === undefined) {
      setQueuedItems([]);
      queuedScope.current = '';
      return;
    }
    const ids = JSON.parse(queuedItemIds) as string[];
    const scope = `${userId}:${collectionId}:${membershipVersion}:${lifecycleVersion}`;
    const sameScope = queuedScope.current === scope;
    setQueuedItems((previous) =>
      sameScope ? previous.filter((item) => ids.includes(item.id)) : [],
    );
    queuedScope.current = scope;
    const stop = ids.map((itemId) =>
      onSnapshot(
        doc(db, 'collections', collectionId, 'items', itemId),
        (snapshot) => {
          if (
            snapshot.metadata?.fromCache ||
            snapshot.metadata?.hasPendingWrites
          )
            return;
          const item = snapshot.exists() ? mapItemDocument(snapshot) : null;
          const record = recordsRef.current.find(
            (entry) => entry.itemId === itemId,
          );
          const missingPublished =
            (!item || item.deleted) &&
            (record?.itemCreated || record?.mutation);
          setQueuedItems((current) => [
            ...current.filter((candidate) => candidate.id !== itemId),
            ...(item
              ? [item]
              : missingPublished && record
                ? [
                    {
                      id: itemId,
                      collectionId,
                      captureOperationId: record.operationId,
                      ...captureDraftFields(record),
                      image: '',
                      createdAt: record.createdAt,
                      deleted: true,
                    },
                  ]
                : []),
          ]);
          if (missingPublished && record && markItemUnavailable)
            void markItemUnavailable(record.operationId).catch(() =>
              notify(
                'Capture status could not be saved on this device. Reopen capture activity to try again.',
                'warning',
              ),
            );
        },
        () => {
          setQueuedItems((current) =>
            current.filter((candidate) => candidate.id !== itemId),
          );
        },
      ),
    );
    return () => stop.forEach((unsubscribe) => unsubscribe());
  }, [
    collectionId,
    lifecycleVersion,
    membershipVersion,
    queuedItemIds,
    markItemUnavailable,
    notify,
    userId,
  ]);
  const capacity = useCollectionCapacityCapability({
    userId,
    enabled:
      captureCollectionId === collectionId ||
      queueOpen ||
      outbox.records.some((record) => record.state !== 'terminal'),
    collectionId: authority?.collectionId,
    membershipVersion: membership?.version,
    role: membership?.role,
  });
  const prefetchAiCapability = outbox.prefetchAiCapability;
  useEffect(() => {
    if (captureCollectionId === collectionId && lifecycle?.state === 'active')
      prefetchAiCapability();
  }, [
    captureCollectionId,
    collectionId,
    lifecycle?.state,
    prefetchAiCapability,
  ]);
  const enqueueDraft = outbox.enqueue;
  const enqueueEdit = outbox.enqueueEdit;
  useEffect(() => {
    const save: ItemEditEnqueue | undefined =
      authority && lifecycle?.state === 'active'
        ? async (item, fields, image, options) => {
            if (item.collectionId !== collectionId)
              throw new Error('Choose an item in this collection.');
            const destination =
              fields.locationId === undefined
                ? undefined
                : locations.find(
                    (entry) =>
                      entry.collectionId === collectionId &&
                      entry.id === fields.locationId,
                  );
            if (fields.locationId !== undefined && !destination)
              throw new Error(
                'Choose an available location in this collection.',
              );
            return enqueueEdit(
              item,
              {
                ...fields,
                ...(destination
                  ? { locationId: destination.id, location: destination.name }
                  : {}),
              },
              image,
              options,
            );
          }
        : undefined;
    registerEdit?.(collectionId, save);
    return () => registerEdit?.(collectionId, undefined);
  }, [
    authority,
    lifecycle?.state,
    collectionId,
    locations,
    enqueueEdit,
    registerEdit,
  ]);
  useEffect(() => {
    const enqueue: CaptureEnqueue | undefined =
      authority && lifecycle?.state === 'active'
        ? async (draft) => {
            const destination = locations.find(
              (entry) =>
                entry.collectionId === collectionId &&
                recordKey(entry) === draft.locationId,
            );
            if (!destination)
              throw new Error('Choose an available location before capturing.');
            if (
              capacity.state.status === 'ready' &&
              !capacity.state.value.additionsAllowed
            ) {
              throw new Error(
                capacity.state.value.admissionBlockedReason === 'debt'
                  ? 'The owner owes credits. Add credits in Billing to resume adding items.'
                  : 'This collection cannot accept items right now.',
              );
            }
            const result = await enqueueDraft({
              ...draft,
              location: destination.name,
              locationId: destination.id,
            });
            capacity.invalidate();
            return result;
          }
        : undefined;
    registerEnqueue(collectionId, enqueue);
    return () => registerEnqueue(collectionId, undefined);
  }, [
    authority,
    capacity,
    collectionId,
    lifecycle?.state,
    locations,
    enqueueDraft,
    registerEnqueue,
  ]);
  const invalidateCapacity = capacity.invalidate;
  const observedFailures = useRef(new Set<string>());
  useEffect(() => {
    const failures = outbox.records.filter(
      (record) => record.state === 'failed',
    );
    const newFailures = failures.filter(
      (record) => !observedFailures.current.has(record.operationId),
    );
    observedFailures.current = new Set(
      failures.map((record) => record.operationId),
    );
    if (!newFailures.length) return;
    const capacityFailures = newFailures.filter(
      (record) => record.lastError?.category === 'capacity',
    );
    if (capacityFailures.length) invalidateCapacity();
    notify(
      capacityFailures.length
        ? `Queued items for ${collection.name} were kept because the collection has no item capacity available.`
        : `${newFailures.length} queued capture${newFailures.length === 1 ? '' : 's'} in ${collection.name} need${newFailures.length === 1 ? 's' : ''} attention.`,
      'warning',
      { label: 'View activity', onClick: () => setQueueOpen(true) },
    );
  }, [
    collection.name,
    invalidateCapacity,
    notify,
    outbox.records,
    setQueueOpen,
  ]);
  const completion = outbox.completion;
  const clearCompletion = outbox.clearCompletion;
  const observedCompletion = useRef<typeof completion>(null);
  useEffect(() => {
    if (!completion || observedCompletion.current === completion) return;
    observedCompletion.current = completion;
    invalidateCapacity();
    clearCompletion();
  }, [clearCompletion, completion, invalidateCapacity]);
  return (
    <>
      <RapidCaptureQueueDialog
        open={queueOpen}
        records={outbox.records}
        items={authoritativeItems}
        online={outbox.online}
        onClose={() => setQueueOpen(false)}
        onRetry={outbox.retry}
        onDiscard={outbox.discard}
        onDismissAI={outbox.dismissAI}
        onEditDraft={(operationId) => {
          const item = draftItems.find(
            (entry) => entry.localCapture?.operationId === operationId,
          );
          if (item) {
            setQueueOpen(false);
            onOpenItem?.(item);
          }
        }}
        onOpenItem={(item) => {
          setQueueOpen(false);
          onOpenItem?.(scopeRecord(item, collection));
        }}
      />
    </>
  );
}

export function MultiCollectionRapidCapture(props: Props) {
  const writable = props.collections.filter(
    (collection) =>
      collectionCapabilities(
        'collection-only',
        collection.role,
        collection.lifecycle,
      ).canEditInventory,
  );
  return (
    <>
      {writable.map((collection) => (
        <CollectionCaptureWorker
          key={`${props.userId}:${collection.collectionId}`}
          {...props}
          collection={collection}
          items={props.items || []}
        />
      ))}
    </>
  );
}
